<template>
  <div>
    <SensitiveMask v-if="customField.sensitive && !isRevealed" @reveal="emit('reveal')" />

    <template v-else>
      <div v-if="customField.field_type === 'rich_text'" class="relative">
        <QuillInput
          :model-value="`${modelValue}`"
          :name="customField.name"
          :label="customField.name"
          :label-sr-only="true"
          @update:model-value="emit('update:modelValue', $event)"
        />
        <HideButton :visible="customField.sensitive" position="top-right" @hide="emit('hide')" />
      </div>

      <div v-else-if="customField.field_type === 'address'" class="relative">
        <AddressInput
          :model-value="`${modelValue}`"
          :hide-label="true"
          @update:model-value="emit('update:modelValue', $event)"
        />
        <HideButton :visible="customField.sensitive" @hide="emit('hide')" />
      </div>

      <div v-else-if="customField.field_type === 'date'" class="relative">
        <DatePicker :model-value="`${modelValue}`" @update:model-value="emit('update:modelValue', $event)" />
        <HideButton :visible="customField.sensitive" position="center-right-offset" @hide="emit('hide')" />
      </div>

      <div v-else-if="customField.field_type === 'multiple_options'">
        <div class="flex flex-col gap-2">
          <label v-for="option in customField.options" :key="option" class="flex items-center gap-2 cursor-pointer">
            <Checkbox
              :model-value="(modelValue as string[]).includes(option)"
              @update:model-value="toggleCheckbox(option, $event as boolean)"
            />
            <span class="text-sm">{{ option }}</span>
          </label>
        </div>
        <HideButton :visible="customField.sensitive" position="inline" @hide="emit('hide')" />
      </div>

      <div v-else-if="customField.field_type === 'single_option'" class="relative">
        <Select :model-value="`${modelValue}`" @update:model-value="emit('update:modelValue', `${$event ?? ''}`)">
          <SelectTrigger class="w-full">
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="option in customField.options" :key="option" :value="option">
              {{ option }}
            </SelectItem>
          </SelectContent>
        </Select>
        <HideButton :visible="customField.sensitive" position="center-right-offset" @hide="emit('hide')" />
      </div>

      <div v-else class="relative">
        <Input
          :model-value="`${modelValue}`"
          :type="inputType"
          @update:model-value="emit('update:modelValue', `${$event}`)"
        />
        <HideButton :visible="customField.sensitive" @hide="emit('hide')" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import QuillInput from '@/components/shared/QuillInput.vue';
import AddressInput from '@/components/shared/AddressInput.vue';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date_picker';
import SensitiveMask from './SensitiveMask.vue';
import HideButton from './HideButton.vue';
import type { CustomFieldEntity } from '@/api/entity_types/CustomField';

type Props = {
  customField: CustomFieldEntity;
  modelValue: string | string[];
  isRevealed: boolean;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: string | string[]];
  reveal: [];
  hide: [];
}>();

const inputType = computed(() => {
  switch (props.customField.field_type) {
    case 'number':
    case 'percentage':
    case 'currency':
      return 'number';
    default:
      return 'text';
  }
});

const toggleCheckbox = (option: string, checked: boolean) => {
  const arr = props.modelValue as string[];
  const newValue = checked ? [...arr, option] : arr.filter(v => v !== option);
  emit('update:modelValue', newValue);
};
</script>
